import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, error, clearError } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    clearError();

    const result = await login({ email, password });
    setIsLoading(false);

    if (result.success) {
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-100">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-slate-100" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-200/50 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-200/50 rounded-full blur-3xl" />

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-50">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(203, 213, 225, 0.5) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(203, 213, 225, 0.5) 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="/Machine-Interface-dark.png"
              alt="ConvoLink Logo"
              className="h-16 w-auto mb-8"
            />

            <h2 className="text-4xl xl:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Transform Your
              <br />
              <span className="text-gradient">Industrial Operations</span>
            </h2>

            <p className="text-lg text-slate-600 mb-8 max-w-md">
              Intelligent automation, predictive maintenance, and conversational
              AI for Industry 4.0
            </p>

            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Active Machines", value: "150+" },
                { label: "Predictions Made", value: "10K+" },
                { label: "Uptime Increase", value: "35%" },
                { label: "Cost Savings", value: "$4.2M" },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  className="bg-white/40 backdrop-blur-lg border border-slate-200/80 rounded-xl p-4"
                >
                  <p className="text-2xl font-bold text-slate-800">
                    {stat.value}
                  </p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="mb-8 lg:hidden">
            <img
              src="/Machine-Interface-dark.png"
              alt="ConvoLink Logo"
              className="h-12 w-auto"
            />
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/60">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Welcome back
              </h2>
              <p className="text-slate-500">Sign in to access your dashboard</p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-danger-500/10 border border-danger-500/20 rounded-xl flex items-center gap-3"
              >
                <AlertCircle className="w-5 h-5 text-danger-400 flex-shrink-0" />
                <p className="text-sm text-danger-400">{error}</p>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 py-2.5 px-4 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-2.5 px-4 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 bg-white text-primary-500 focus:ring-primary-500 focus:ring-offset-2"
                  />
                  <span className="text-sm text-slate-600">Remember me</span>
                </label>
                <a
                  href="#"
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary w-full py-3"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-500 text-center mb-4">
                Demo Credentials
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setEmail("admin@convolink.com");
                    setPassword("password123");
                  }}
                  className="p-2 bg-slate-100 rounded-lg text-slate-500 hover:text-primary-600 hover:bg-primary-500/10 transition-colors"
                >
                  Admin
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEmail("operator@convolink.com");
                    setPassword("password123");
                  }}
                  className="p-2 bg-slate-100 rounded-lg text-slate-500 hover:text-primary-600 hover:bg-primary-500/10 transition-colors"
                >
                  Operator
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEmail("technician@convolink.com");
                    setPassword("password123");
                  }}
                  className="p-2 bg-slate-100 rounded-lg text-slate-500 hover:text-primary-600 hover:bg-primary-500/10 transition-colors"
                >
                  Technician
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEmail("manager@convolink.com");
                    setPassword("password123");
                  }}
                  className="p-2 bg-slate-100 rounded-lg text-slate-500 hover:text-primary-600 hover:bg-primary-500/10 transition-colors"
                >
                  Manager
                </button>
              </div>
            </div> */}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;